import * as React from 'react';
import * as Yup from 'yup';
import { useTranslations, Trans } from '@mentimeter/i18n';
import { TextInput, Form, Check, Select } from '@mentimeter/ragnar-form';
import { Meta } from 'src/ui/typography';
import { Button, Link } from 'src/ui/actions';
import { Box } from '@mentimeter/ragnar-ui/box';
import type { FormValuesT, StatusT } from './types';

interface PropsT {
  formFields: FormValuesT;
  status: StatusT;
  handleSubscribe: (data: FormValuesT) => void;
  buttonTitle: string;
  consent?: boolean;
  audience: 'enterprise' | 'education';
  school?: string;
}

export const NotifyForm = ({
  formFields,
  status,
  buttonTitle,
  handleSubscribe,
  consent,
  audience,
}: PropsT) => {
  const t = useTranslations('common');

  const {
    email,
    firstName,
    lastName,
    jobTitle,
    company,
    jobSeniority,
    school,
    subscriptionGroup,
  } = formFields;

  const hasFirstNameField = 'firstName' in formFields;
  const hasLastNameField = 'lastName' in formFields;
  const hasJobTitleField = 'jobTitle' in formFields;
  const hasJobSeniorityField = 'jobSeniority' in formFields;
  const hasCompanyField = 'company' in formFields;
  const isEducation = audience === 'education';
  const prefix = isEducation ? 'edu' : 'job';

  const SELECT_DEFAULT_VALUE = {
    key: 'defaultValue',
    value: '',
    label: t(`notify_form.${prefix}_seniority_select`),
  };

  const keyEnterpriseTranslations = {
    'C-Level': t('notify_form.job_seniority_select_option_0'),
    'Department Lead': t('notify_form.job_seniority_select_option_1'),
    'Team Lead': t('notify_form.job_seniority_select_option_2'),
    'Individual Contributor': t('notify_form.job_seniority_select_option_3'),
    Other: t('notify_form.job_seniority_select_option_4'),
  };

  const selectEnterpriseOptions = [
    SELECT_DEFAULT_VALUE,
    ...Object.entries(keyEnterpriseTranslations).map(([key, value]) => ({
      key,
      value: key,
      label: value,
    })),
  ];

  const keyEducationTranslations = {
    'Chancellor/Provost': t('notify_form.job_seniority_edu_select_option_0'),
    'Department Head': t('notify_form.job_seniority_edu_select_option_1'),
    'University Administrator': t(
      'notify_form.job_seniority_edu_select_option_2',
    ),
    'Program Manager': t('notify_form.job_seniority_edu_select_option_3'),
    Faculty: t('notify_form.job_seniority_edu_select_option_4'),
    Student: t('notify_form.job_seniority_edu_select_option_5'),
    Other: t('notify_form.job_seniority_edu_select_option_6'),
  };

  const selectEducationOptions = [
    SELECT_DEFAULT_VALUE,
    ...Object.entries(keyEducationTranslations).map(([key, value]) => ({
      key,
      value: key,
      label: value,
    })),
  ];

  const generateValidationSchema = () => ({
    email: Yup.string()
      .email(t('notify_form.validation.email_invalid'))
      .required(t(`notify_form.validation.${prefix}_email_required`))
      .matches(
        /^(?!.*gmail|.*hotmail|.*yahoo|.*outlook).*$/,
        t(`notify_form.validation.${prefix}_email_required`),
      )
      .max(150, t('notify_form.validation.email_long')),

    ...(hasFirstNameField && {
      firstName: Yup.string()
        .required(t('notify_form.validation.first_name_required'))
        .max(150, t('notify_form.validation.first_name_long')),
    }),
    ...(hasLastNameField && {
      lastName: Yup.string()
        .required(t('notify_form.validation.last_name_required'))
        .max(150, t('notify_form.validation.last_name_long')),
    }),
    ...(!isEducation && {
      ...(hasJobTitleField && {
        jobTitle: Yup.string()
          .required(t('notify_form.validation.job_title_required'))
          .max(150, t('notify_form.validation.job_title_long')),
      }),
      ...(hasJobSeniorityField && {
        jobSeniority: Yup.string().required(
          t('notify_form.validation.job_seniority_required'),
        ),
      }),
    }),
    ...(isEducation && {
      eduSeniority: Yup.string().required(
        t('notify_form.validation.edu_seniority_required'),
      ),
      school: Yup.string()
        .required(t('notify_form.validation.school_required'))
        .max(150, t('notify_form.validation.school_long')),
    }),
    ...(hasCompanyField &&
      !isEducation && {
        company: Yup.string()
          .required(t('notify_form.validation.company_required'))
          .max(150, t('notify_form.validation.company_long')),
      }),
    ...(consent && {
      consent: Yup.boolean().oneOf(
        [true],
        t('notify_form.validation.consent_required'),
      ),
    }),
  });

  const validationSchema = Yup.object().shape(generateValidationSchema());

  return (
    <>
      <Form
        initialValues={{
          email,
          firstName,
          lastName,
          jobTitle,
          jobSeniority,
          company,
          school,
          targetAudience: audience,
          subscriptionGroup,
          consent: false,
        }}
        autoComplete="on"
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={handleSubscribe}
        width="100%"
        alignItems="center"
      >
        <Box flexDirection="row" justifyContent="space-between" width="100%">
          {hasFirstNameField && (
            <TextInput
              name="firstName"
              maxWidth="48%"
              autoComplete="given-name"
              label={t('notify_form.first_name')}
              width={1}
            />
          )}
          {hasLastNameField && (
            <TextInput
              name="lastName"
              maxWidth="48%"
              autoComplete="family-name"
              label={t('notify_form.last_name')}
              width={1}
            />
          )}
        </Box>
        <TextInput
          name="email"
          autoComplete="email"
          label={t(`notify_form.${prefix}_email`)}
          width={1}
          disabled={Boolean(email)}
        />
        {hasJobTitleField && !isEducation && (
          <TextInput
            name="jobTitle"
            autoComplete="organization-title"
            label={t('notify_form.job_title')}
            width={1}
          />
        )}
        {hasJobSeniorityField && !isEducation && (
          <Select
            name="jobSeniority"
            aria-autocomplete="list"
            label={t('notify_form.job_seniority')}
            width="100%"
            defaultValue={SELECT_DEFAULT_VALUE.value}
            options={selectEnterpriseOptions}
          />
        )}
        {isEducation && (
          <Select
            name="eduSeniority"
            aria-autocomplete="list"
            label={t('notify_form.edu_seniority')}
            width="100%"
            defaultValue={SELECT_DEFAULT_VALUE.value}
            options={selectEducationOptions}
          />
        )}
        {isEducation && (
          <TextInput
            name="school"
            autoComplete="organization"
            label={t('notify_form.school')}
            width={1}
          />
        )}
        {hasCompanyField && !isEducation && (
          <TextInput
            name="company"
            autoComplete="organization"
            label={t('notify_form.company')}
            width={1}
          />
        )}
        {consent && (
          <Check
            id="consent"
            name="consent"
            label={t('notify_form.consent')}
            extend={() => ({
              '& label': {
                marginBottom: 0,
              },
            })}
          />
        )}
        <Button
          mb="space8"
          mt="space2"
          type="submit"
          state={status}
          variant="primary"
        >
          {buttonTitle}
        </Button>
        <input type="hidden" name="targetAudience" value={audience} />
        <input
          type="hidden"
          name="subscriptionGroup"
          value={subscriptionGroup}
        />
      </Form>
      <Meta textAlign="center" mb={['space8', null, 'space0']}>
        <Trans
          t={t}
          i18nKey="notify_form.accept_terms_and_policies"
          components={{
            terms: (
              <Link href="/terms" target="_blank" rel="noopener noreferrer" />
            ),
            policies: (
              <Link
                href="/policies"
                target="_blank"
                rel="noopener noreferrer"
              />
            ),
          }}
        />
      </Meta>
    </>
  );
};
